import React, { useEffect, useRef, useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import NearMeIcon from "@material-ui/icons/NearMe";
import Pexels from "../../img/avi.png";
import "./message.css";
import MessageService from "../../services/message.service";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en.json";

import { io } from "socket.io-client";
import UserService from "../../services/user.service";
import { baseURL, socketURL } from "../../common/constants.js";

TimeAgo.setDefaultLocale(en.locale);
TimeAgo.addLocale(en);

const StyledBadge = withStyles((theme) => ({
	badge: {
		backgroundColor: "#44b700",
		color: "#44b700",
		boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
		"&::after": {
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			borderRadius: "50%",
			animation: "$ripple 1.2s infinite ease-in-out",
			border: "1px solid currentColor",
			content: '""',
		},
	},
	"@keyframes ripple": {
		"0%": {
			transform: "scale(.8)",
			opacity: 1,
		},
		"100%": {
			transform: "scale(2.4)",
			opacity: 0,
		},
	},
}))(Badge);

const useStyles = makeStyles((theme) => ({
	messageContainer: {
		padding: "10px",
		// overflowY: "auto",
		// maxHeight: "100vh",
		border: "1px solid rgba(144, 25, 25, 0.02)",
		boxShadow: "0px 4px 4px rgba(144, 25, 25, 0.05)",
		borderRadius: "10px",
		position: "relative",
	},
	messageHeader: {
		display: "flex",
		justifyContent: "space-between",
		padding: "5px",
	},
	messageHead: {
		display: "flex",
		padding: "6px",
	},
	messageContent: {
		marginLeft: "10px",
		marginRight: "10px",
	},
	messageUsername: {
		fontSize: "16px",
		fontWeight: "bold",
	},
	messageStatus: {
		fontStyle: "italic",
	},
}));

const Message = ({ currentUser, receiver, setReceiver, currentChat }) => {
	const classes = useStyles();
	const [newMessages, setNewMessages] = useState("");
	const [messages, setMessages] = useState([]);
	const [arrivalMessage, setArrivalMessage] = useState(null);
	const [allContacts, setALLContacts] = useState([]);
	const [isOnline, setIsOnline] = useState(false);
	// const [isRecent, setIsRecent] = useState(false);
	const [receiverData, setReceiverData] = useState(null);
	const [onlineUsers, setOnlineUsers] = useState([]);
	const scrollRef = useRef();
	const socket = useRef();
	const timeAgo = new TimeAgo();

	useEffect(() => {
		let componentMounted = true;
		if (componentMounted) {
			setIsOnline(onlineUsers.includes(receiverData?._id));
		}

		return () => componentMounted = false;
	}, [onlineUsers, receiverData?._id]);

	useEffect(() => {
		let componentMounted = true;
		if (componentMounted) {
			socket.current = io(socketURL, {
				withCreditentials: true,
			});

			socket.current.on("getMessage", (data) => {
				setArrivalMessage({
					sender: data.senderId,
					text: data.text,
					createdAt: Date.now(),
				});
			});
		}

		return () => componentMounted = false;
	}, []);

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;
		(async () => {
			const res = await UserService.getContacts(signal);
			setALLContacts(res.data);
		})();

		return () => controller.abort();
	 }, []);

	useEffect(() => {
		socket.current.emit("addUser", currentUser._id);
		socket.current.on("getUsers", (users) => {
			setOnlineUsers(
				allContacts?.filter((c) => users.some((u) => u.userId === c)),
			);
		});
	}, [currentUser, allContacts]);

	useEffect(() => {
		arrivalMessage &&
			currentChat?.members.includes(arrivalMessage.sender) &&
			setMessages((prev) => [...prev, arrivalMessage]);
	}, [arrivalMessage, currentChat]);

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;
		(async () => {
			const res = await MessageService.getMessages(currentChat?._id, signal);
			setMessages(res.messages);
		})();

		return () => controller.abort();
	}, [currentChat?._id]);

	useEffect(() => {
		const controller = new AbortController();
		const signal = controller.signal;
			(async () => {
				if (currentChat && currentUser) {
					const res = await UserService.getLoginUser(
						currentChat?.members.find((member) => member !== currentUser._id),
						signal,
					);
					setReceiverData(res?.user);
				}
			})();
	
		return () => controller.abort();
	}, [currentChat, currentUser]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		const message = {
			sender: currentUser._id,
			text: newMessages,
			conversationId: currentChat._id,
		};

		const receiverId = currentChat?.members.find(
			(member) => member !== currentUser._id,
		);

		socket.current.emit("sendMessage", {
			senderId: currentUser._id,
			receiverId,
			text: newMessages,
		});

		const res = await MessageService.sendMessages(message);
		setMessages([...messages, res.savedMessage]);
		setNewMessages("");
	};

	useEffect(() => {
		scrollRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	return (
		<div className={classes.messageContainer}>
			{currentChat ? (
				<>
					<div className="message-body">
						<div className={classes.messageHeader}>
							<div className={classes.messageHead}>
								<div className={classes.chatImage}>
									<StyledBadge
										overlap="circular"
										anchorOrigin={{
											vertical: "bottom",
											horizontal: "right",
										}}
										variant={isOnline ? "dot" : "standard"}
									>
										{receiverData?.avatar ? (
											<Avatar
												alt="User Avatar"
												src={`${baseURL}/avatar/image/${receiverData?.avatar}`}
												className={classes.avatarImage}
											/>
										) : (
											<Avatar
												alt="User Avatar"
												src={Pexels}
												className={classes.avatarImage}
											/>
										)}
									</StyledBadge>
								</div>
								<div className={classes.messageContent}>
									<div className={classes.messageUsername}>
										{receiverData?.firstname} {receiverData?.lastname}
									</div>
									{isOnline && <p className={classes.messageStatus}>Online</p>}
								</div>
							</div>

							<div className={classes.iconContainer}>
								<div className={classes.moreOption}>
									<IconButton>
										<MoreVertIcon />
									</IconButton>
								</div>
							</div>
						</div>

						<div className="chat-container">
							{messages?.map((message, idx) => (
								<div key={message ? message._id : idx} ref={scrollRef}>
									{message?.sender === currentUser?._id ? (
										<>
											<div className="message-body-sender">{message?.text}</div>
											<span className="message-body-sender__timestamp">
												{timeAgo.format(new Date(message.createdAt), "mini")}
											</span>
										</>
									) : (
										<>
											<div className="message-body-receiver">{message?.text}</div>
											<span className="message-body-receiver__timestamp">
												{timeAgo.format(new Date(message.createdAt), "mini")}
											</span>
										</>
									)}
								</div>
							))}
						</div>
					</div>

					<div className="holder-container">
						<div className="holder">
							<form className="form-container" noValidate autoComplete="off">
								<IconButton className="emojiButton">
									<InsertEmoticonIcon />
								</IconButton>

								<textarea
									className="text-area-box"
									placeholder="Type a Message....."
									onChange={(e) => setNewMessages(e.target.value)}
									value={newMessages}
								></textarea>

								<IconButton className="sendButton" onClick={handleSubmit}>
									<NearMeIcon />
								</IconButton>
							</form>
						</div>
					</div>
				</>
			) : (
				<div className="noConvo">
					<span>Open a conversation to start chat</span>
				</div>
			)}
		</div>
	);
};

export default Message;
